import { get, last, toNumber } from 'lodash';
import { createSelector } from 'reselect';

import {
  MAX_POSTS_PER_PAGE,
  MAX_POSTS_PER_PAGE_FULL_POST,
  MAX_POSTS_PER_PAGE_MOBILE,
  PaginationType,
  POSTS_PER_PAGE,
  getPaginationTypeParamPath,
  getPaginationPageSizeParamPath,
  isMultiplePagesSupported,
  buildPaginationRequestParams,
  type Section,
} from '@wix/communities-blog-client-common';
import { getIsInitialRoute } from '../router/router-selectors';
import { getIsMobile, isSeo, isSite } from '../store/basic-params/basic-params-selectors';
import { type AppState } from '../types';
import { getAppSettingsValue } from './app-settings-base-selectors';
import { isFullPostLayout, getLayoutType } from './layout-selectors';

import { getSection } from './section-selectors';

type EntityPagination<T = any> = {
  entitiesByPage: { [page: string]: T[] };
  entityCount: number;
  currentPage: number;
  pageSize: number;
  cursor: string | null;
};

const getPagination = (state: { pagination: Record<string, EntityPagination> }) => state.pagination;

const getEntityPagination = createSelector(
  [getPagination, (_, entityType: string) => entityType],
  (pagination, entityType) => get(pagination, entityType, {} as EntityPagination),
);

export const getEntitiesByPage = createSelector(
  [getEntityPagination],
  (entityPagination) => entityPagination.entitiesByPage,
);

export const getEntityCount = createSelector(
  [getEntityPagination],
  (entityPagination) => entityPagination.entityCount,
);

export const getEntityCursor = createSelector(
  [getEntityPagination],
  (entityPagination): string | null => {
    const cursor = entityPagination.cursor;
    if (!cursor || cursor === 'undefined') {
      return null;
    }
    return cursor;
  },
);

/**
 * Infinite-scroll load-more uses the adapter next cursor (limit+1), not totals.
 * Falls back to entityCount while a total is still present and cursor is absent.
 */
export const hasMorePosts = ({
  cursor,
  loadedCount,
  entityCount,
}: {
  cursor?: string | null;
  loadedCount: number;
  entityCount?: number;
}) => {
  if (cursor && cursor !== 'undefined') {
    return true;
  }

  return typeof entityCount === 'number' && entityCount > 0 && loadedCount < entityCount;
};

export const getCurrentPage = createSelector(
  [getEntityPagination],
  (entityPagination) => entityPagination.currentPage || 1,
);

export const getCurrentPageEntities = createSelector(
  [getEntitiesByPage, getCurrentPage],
  (entitiesByPage, page) => get(entitiesByPage, page),
);

export const getLastPageIndex = createSelector(
  [getEntitiesByPage],
  (entitiesByPage) => toNumber(last(Object.keys(entitiesByPage).sort())) || 1,
);

export const getPageSize = (
  state: AppState,
  {
    overrideSettingsPageSize = undefined,
    section,
  }: {
    section?: Section;
    overrideSettingsPageSize?: number;
  } = {},
) => {
  let maxPageSize: number = MAX_POSTS_PER_PAGE;

  if (
    getIsMobile(state) &&
    getPaginationType(state, section) === PaginationType.InfiniteScroll &&
    !isSeo(state)
  ) {
    maxPageSize = MAX_POSTS_PER_PAGE_MOBILE;
  } else if (isFullPostLayout(state, section)) {
    maxPageSize = MAX_POSTS_PER_PAGE_FULL_POST;
  }
  return Math.min(overrideSettingsPageSize || getPaginationPageSize(state, section), maxPageSize);
};

export const getPaginationType = (state: AppState, section?: Section) => {
  section ||= getSection(state, true);
  const paginationType = getAppSettingsValue({
    state,
    key: getPaginationTypeParamPath(section),
    fallback: PaginationType.InfiniteScroll,
  });
  const layoutType = getLayoutType(state, section);
  if (paginationType === PaginationType.MultiplePages && isMultiplePagesSupported(layoutType)) {
    return paginationType;
  }
  return PaginationType.InfiniteScroll;
};

export const getPaginationPageSize = (state: AppState, _section?: Section) => {
  const section = _section || getSection(state, true);
  return getAppSettingsValue({
    state,
    key: getPaginationPageSizeParamPath(section),
    fallback: POSTS_PER_PAGE,
  });
};

export const getShowPagination = (state: AppState, section: Section) => {
  const paginationType = getPaginationType(state, section);
  return paginationType === PaginationType.MultiplePages;
};

/**
 * Whether the requested `/page/:page` URL points past the end of the feed.
 *
 * Takes `page` from the caller rather than reading `getCurrentPage`, because
 * `fetchFeedPostsRequest` is a bare `createAction` called as
 * `fetchFeedPostsRequest(entityType, page)` — the page argument is dropped, so
 * the feed's `currentPage` is always 1 no matter which page was requested.
 *
 * Decided by the fetched page coming back empty rather than by comparing the
 * page against `entityCount / pageSize`, because the requested page size is
 * capped (`getPageSize`) while the setting is not, so the computed page count
 * can be smaller than what the feed actually serves.
 *
 * Page 1 is never out of range, so an empty blog still renders its empty state.
 * A page that was never fetched (a failed request leaves no entry) is also not
 * out of range, so that a 404 cannot mask the request's own error status.
 */
export const getIsPageOutOfRange = (state: AppState, page: number) => {
  if (page <= 1) {
    return false;
  }

  const entities = get(getEntitiesByPage(state, 'posts'), page);
  return Array.isArray(entities) && entities.length === 0;
};

export const getIsLazyPaginationEnabled = (state: AppState, section?: Section) => {
  section ??= getSection(state, true);

  return (
    !isSeo(state) &&
    isSite(state) &&
    getIsMobile(state) &&
    getShowPagination(state, section) &&
    getPageSize(state, { section }) > MAX_POSTS_PER_PAGE_MOBILE
  );
};

export const getLazyPaginationParams = ({
  state,
  page,
  section,
}: {
  state: AppState;
  page: number;
  section: Section;
}) => {
  if (!getIsLazyPaginationEnabled(state, section) || !getIsInitialRoute(state)) {
    return;
  }

  const pageSize = getPageSize(state, { section });
  const paginationParams = buildPaginationRequestParams(page, pageSize);
  if (page === 1) {
    paginationParams.size = MAX_POSTS_PER_PAGE_MOBILE;
  }
  return paginationParams;
};
